import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AuthLayout } from '../../components/AuthLayout';
import { Button } from '@werkstatt/ui';
import { Input } from '@werkstatt/ui';
import { Label } from '@werkstatt/ui';
import { Alert, AlertDescription } from '@werkstatt/ui';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <AuthLayout title="Registrierung nicht möglich">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Kein Registrierungstoken gefunden. Bitte verwenden Sie den Link aus Ihrer Willkommens-E-Mail.
          </AlertDescription>
        </Alert>
        <Link to="/login" className="text-sm text-primary hover:underline">
          Zur Anmeldung
        </Link>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== passwordConfirm) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    if (password.length < 8) {
      setError('Das Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setIsLoading(true);
    try {
      await api.post('/auth/register', {
        token,
        first_name: firstName,
        last_name: lastName,
        password,
      });
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
      setError(detail || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <AuthLayout>
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <CheckCircle2 className="h-16 w-16 text-primary" />
          </div>
          <h2 className="font-display text-2xl font-bold text-foreground">Konto erfolgreich erstellt!</h2>
          <p className="text-sm text-muted-foreground">
            Sie werden in wenigen Sekunden zur Anmeldung weitergeleitet...
          </p>
          <Link to="/login" className="text-sm text-primary hover:underline">
            Jetzt anmelden
          </Link>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Konto einrichten"
      subtitle="Geben Sie Ihre persönlichen Daten ein und legen Sie ein Passwort fest."
    >
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="firstName">Vorname</Label>
            <Input
              id="firstName"
              type="text"
              placeholder="Max"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="given-name"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lastName">Nachname</Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Mustermann"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isLoading}
              required
              autoComplete="family-name"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Passwort</Label>
          <Input
            id="password"
            type="password"
            placeholder="Mindestens 8 Zeichen"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            required
            autoComplete="new-password"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="passwordConfirm">Passwort bestätigen</Label>
          <Input
            id="passwordConfirm"
            type="password"
            placeholder="Passwort wiederholen"
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            disabled={isLoading}
            required
            autoComplete="new-password"
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Konto wird erstellt...' : 'Konto erstellen'}
        </Button>
      </form>

      <div className="text-center text-sm text-muted-foreground">
        <Link to="/login" className="text-primary hover:underline">
          Zurück zur Anmeldung
        </Link>
      </div>
    </AuthLayout>
  );
}
